import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useToast } from './Toast';
import { addWatermarkToImage, generatedImageDownloadFilename } from './Watermark';
import { RunningHubCosExpiryBadge } from './RunningHubCosExpiryBadge';
import { RUNNINGHUB_OUTPUT_MAX_AGE_MS, RUNNINGHUB_LOCAL_HISTORY_MAX_ITEMS } from '../utils/runningHubRetention';
import {
  createScopedRunningHubClientItemId,
  loadRunningHubHistoryForDisplay,
  syncRunningHubHistoryItemToCloud,
  deleteRunningHubHistoryFromCloud,
} from '../utils/runningHubCloudHistory';
import { isRunningHubListThumbUrl } from '../utils/runningHubHistoryLocalStorage';
import {
  writeRunningHubHistoryListToLocalStorage,
  stampRunningHubHistoryOwner,
} from '../utils/runningHubHistoryScope';
import { RunningHubHistoryListThumb } from './RunningHubHistoryListThumb';
import { useRunningHubLinkIndicator } from '../utils/useRunningHubLinkIndicator';
import { useCreditDeduction } from '../hooks/useCreditDeduction';
import { useRunningHubPanelHistory } from '../hooks/useRunningHubPanelHistory';
import type { Lang } from '../utils/i18n';
import { api } from '../services/apiClient';
import {
  startCompareSliderPointerDragFromWrapper,
  startCompareSliderTouchDragFromWrapper,
} from '../utils/compareSliderPointer';
import { attachComparePanelPinchZoom } from '../utils/comparePanelPinchZoom';
import { createRunningHubClientItemId, type RunningHubRecoveryRequest } from '../utils/applyRunningHubJobRecovery';
import { useRunningHubPanelRecovery } from '../hooks/useRunningHubPanelRecovery';
import {
  isInlineDataImageUrl,
  persistPhysicalOriginalImageUrl,
  resolveUsableHistoryOriginalImage,
} from '../utils/physicalReconstructOriginalImage';
import {
  filterPhysicalReconstructHistoryForDisplay,
  isAggregatePhysicalReconstructHistoryId,
  normalizePhysicalReconstructModeLabel,
} from '../utils/physicalReconstructHistoryFilter';
import {
  getUnsupportedUploadImageFormatMessage,
  isSupportedUploadImageDataUrl,
  isSupportedUploadImageFile,
} from '../utils/supportedUploadImageFormat';

interface PhysicalReconstructPanelProps {
  isOpen: boolean;
  onClose: () => void;
  sourceImage: string | null;
  isDarkMode: boolean;
  language: Lang;
  userEmail?: string | null;
  isAuthenticated?: boolean;
  recoveryRequest?: RunningHubRecoveryRequest | null;
}

/** 物理重构：RunningHub（.env RUNNINGHUB_PHYSICAL_RECONSTRUCT_*，一次返回 4 张） */
const PhysicalReconstructPanel: React.FC<PhysicalReconstructPanelProps> = ({
  isOpen,
  onClose,
  sourceImage,
  isDarkMode,
  language,
  userEmail,
  isAuthenticated = false,
  recoveryRequest = null,
}) => {
  const { showToast } = useToast();
  
  // 积分扣费 Hook
  const { deduct, isDeducting: isDeductingCredits } = useCreditDeduction({
    language: language === 'en' ? 'en' : 'zh',
    onInsufficientBalance: (required, available) => {
      showToast(
        language === 'en' 
          ? `Insufficient credits: need ${required}, have ${available}. Please recharge.` 
          : `积分不足：需要 ${required} 积分，当前 ${available} 积分，请及时充值`,
        'warning'
      );
    },
    onDeductionError: (error) => {
      showToast(
        language === 'en' ? `Credit deduction failed: ${error}` : `扣费失败：${error}`,
        'error'
      );
    }
  });

  type Mode = 'day' | 'cloudy' | 'night' | 'dusk';
  const [isProcessing, setIsProcessing] = useState(false);
  const generationInFlightRef = useRef(false);
  const [progressDesc, setProgressDesc] = useState('');
  const [progressPercent, setProgressPercent] = useState(0);
  const [resultUrl, setResultUrl] = useState<string | null>(null);
  /** 左侧「输入源」预览：仅上传/拖入/首次从主程序传入时更新 */
  const [inputSourceImage, setInputSourceImage] = useState<string | null>(null);
  /** 右侧对比区底图：历史选中时可与输入源不同；不反向覆盖 inputSourceImage */
  const [compareOriginalImage, setCompareOriginalImage] = useState<string | null>(null);
  const inputSourceSeededRef = useRef(false);
  const [sliderPosition, setSliderPosition] = useState(50);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [panX, setPanX] = useState(0);
  const [panY, setPanY] = useState(0);
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [history, setHistory] = useState<
    Array<{ id: string; originalImage: string; upscaledUrl: string; modeLabel?: string; timestamp: number; thumbUrl?: string }>
  >([]);
  const [selectedHistoryId, setSelectedHistoryId] = useState<string | null>(null);
  const runningHubLinked = useRunningHubLinkIndicator(isOpen);
  const controlsDisabled = isProcessing || isDeductingCredits;

  const containerRef = useRef<HTMLDivElement>(null);
  const imageContainerRef = useRef<HTMLDivElement>(null);
  const originalImageRef = useRef<HTMLImageElement>(null);
  const upscaledImageRef = useRef<HTMLImageElement>(null);
  const imageWrapperRef = useRef<HTMLDivElement>(null);
  const zoomLevelPinchRef = useRef(1);
  const fileInputRef = useRef<HTMLInputElement>(null);

  zoomLevelPinchRef.current = zoomLevel;

  const t = (en: string, zh: string) => (language === 'en' ? en : zh);

  const historyOriginalMissing =
    !!selectedHistoryId &&
    !resolveUsableHistoryOriginalImage(history.find((x) => x.id === selectedHistoryId) ?? {});

  const inputSourceDisplay = selectedHistoryId
    ? inputSourceImage
    : (inputSourceImage ?? sourceImage);

  const compareOriginalDisplay = selectedHistoryId
    ? compareOriginalImage
    : (compareOriginalImage ?? inputSourceImage ?? sourceImage);

  const modeLabelByMode: Record<Mode, string> = {
    day: t('PHYSICAL PHOTO / DAY', '物理照片/白天'),
    cloudy: t('PHYSICAL PHOTO / CLOUDY', '物理照片/阴天'),
    night: t('PHYSICAL PHOTO / NIGHT', '物理照片/夜晚'),
    dusk: t('PHYSICAL PHOTO / DUSK', '物理照片/黄昏'),
  };

  /**
   * RunningHub 一次返回 4 张：这里仅维护“下标 -> 编号”的稳定映射：
   * 0=01D、1=02D、2=03D、3=04D。
   */
  const outputOrder: Mode[] = ['dusk', 'day', 'night', 'cloudy'];

  /** 红框“物理重构历史”列表显示：按返回数组下标固定编号，不再做语义对齐 */
  const historyLabelByIndex = (i: number) => {
    if (i === 0) return '01D';
    if (i === 1) return '02D';
    if (i === 2) return '03D';
    if (i === 3) return '04D';
    return '0XD';
  };

  const applyPhysicalHistoryDisplayFilter = <
    T extends { id: string; modeLabel?: string; timestamp: number },
  >(
    items: T[],
  ): T[] => filterPhysicalReconstructHistoryForDisplay(items);

  const getImageDimensions = (dataUrl: string): Promise<{ width: number; height: number }> =>
    new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve({ width: img.naturalWidth || 1024, height: img.naturalHeight || 1024 });
      img.onerror = () => resolve({ width: 1024, height: 1024 });
      img.src = dataUrl;
    });

  const readFileAsDataUrl = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(typeof r.result === 'string' ? r.result : '');
      r.onerror = () => reject(r.error ?? new Error('read failed'));
      r.readAsDataURL(file);
    });

  const replaceCurrentImageFromFile = async (file: File) => {
    if (!file || !isSupportedUploadImageFile(file)) {
      showToast(getUnsupportedUploadImageFormatMessage(language === 'en' ? 'en' : 'zh'), 'warning');
      return;
    }
    try {
      const dataUrl = await readFileAsDataUrl(file);
      if (!dataUrl) throw new Error('empty');
      setInputSourceImage(dataUrl);
      inputSourceSeededRef.current = true;
      setCompareOriginalImage(null);
      setResultUrl(null);
      setSelectedHistoryId(null);
      showToast(t('Image loaded', '图片已载入'), 'success');
    } catch {
      showToast(t('Failed to load image', '载入图片失败'), 'error');
    } finally {
      // 允许再次选择同一文件
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const onPickFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) await replaceCurrentImageFromFile(f);
  };

  const appendPhysicalHistory = (
    originalImage: string,
    urls: string[],
    opts?: { skipCloudSync?: boolean; clientItemId?: string; skipCloudSyncIds?: string[] },
  ) => {
    const baseTs = Date.now();
    const cid = opts?.clientItemId?.trim();
    const skipIds = new Set(opts?.skipCloudSyncIds ?? []);
    const newItems = outputOrder
      .map((m, i) =>
        stampRunningHubHistoryOwner(
          {
            id: cid
              ? `${cid}-${i}`
              : createScopedRunningHubClientItemId('physical_reconstruct', userEmail, !!isAuthenticated),
            originalImage,
            upscaledUrl: urls[i] || '',
            modeLabel: historyLabelByIndex(i),
            timestamp: baseTs + i,
          },
          userEmail,
        ),
      )
      .filter(
        (x): x is NonNullable<typeof x> =>
          x !== null && typeof x.upscaledUrl === 'string' && !!x.upscaledUrl,
      );
    if (!newItems.length) return;

    setHistory((prev) => {
      const cleaned = cleanExpired(prev);
      const updated = applyPhysicalHistoryDisplayFilter(
        [...newItems, ...cleaned].slice(0, RUNNINGHUB_LOCAL_HISTORY_MAX_ITEMS),
      );
      writeRunningHubHistoryListToLocalStorage(
        'physical_reconstruct',
        userEmail,
        updated as unknown as Record<string, unknown>[],
      );
      if (!opts?.skipCloudSync) {
        void (async () => {
          let lastErr: string | undefined;
          const idToThumb = new Map<string, string>();
          for (const it of newItems) {
            if (skipIds.has(it.id)) continue;
            const r = await syncRunningHubHistoryItemToCloud('physical_reconstruct', !!isAuthenticated, it.id, it, {
              userEmailForLog: userEmail,
              currentUserEmail: userEmail,
            });
            if (!r.ok && !r.skipped) lastErr = r.error;
            if (r.thumbUrl && isRunningHubListThumbUrl(r.thumbUrl)) idToThumb.set(it.id, r.thumbUrl);
          }
          if (lastErr) {
            showToast(
              language === 'en' ? `Cloud archive failed: ${lastErr}` : `云端存档失败：${lastErr}`,
              'error',
            );
          }
          if (idToThumb.size) {
            setHistory((cur) => {
              const mapped = applyPhysicalHistoryDisplayFilter(
                cur.map((x) => (idToThumb.has(x.id) ? { ...x, thumbUrl: idToThumb.get(x.id) } : x)),
              );
              writeRunningHubHistoryListToLocalStorage(
                'physical_reconstruct',
                userEmail,
                mapped as unknown as Record<string, unknown>[],
              );
              return mapped;
            });
          }
        })();
      }
      return updated;
    });
  };

  const cleanExpired = (
    items: Array<{ id: string; originalImage: string; upscaledUrl: string; modeLabel?: string; timestamp: number }>,
  ) => {
    const now = Date.now();
    const filtered = items.filter((item) => now - item.timestamp < RUNNINGHUB_OUTPUT_MAX_AGE_MS);
    return applyPhysicalHistoryDisplayFilter(
      filtered.slice(0, RUNNINGHUB_LOCAL_HISTORY_MAX_ITEMS),
    );
  };

  /** 服务端已分片归档后：单次云端拉取，覆盖本地列表（已过滤 01D–04D） */
  const refreshPhysicalHistoryFromCloudOnce = async () => {
    if (!isAuthenticated || !userEmail?.trim()) return;
    const loaded = await loadRunningHubHistoryForDisplay(
      'physical_reconstruct',
      true,
      userEmail,
      cleanExpired,
      { userEmailForLog: userEmail, currentUserEmail: userEmail },
    );
    const filtered = applyPhysicalHistoryDisplayFilter(loaded.items);
    if (filtered.length > 0) {
      setHistory(filtered);
      writeRunningHubHistoryListToLocalStorage(
        'physical_reconstruct',
        userEmail,
        filtered as unknown as Record<string, unknown>[],
      );
    }
  };

  useRunningHubPanelHistory({
    featureType: 'physical_reconstruct',
    isOpen,
    isAuthenticated: !!isAuthenticated,
    userEmail,
    language,
    showToast,
    setHistory,
    cleanExpired,
    onBeforeClear: () => {
      setSelectedHistoryId(null);
      setResultUrl(null);
      setCompareOriginalImage(null);
    },
    mapLoadedItems: (items) => applyPhysicalHistoryDisplayFilter(items),
    keepHistoryOnSuccessfulCloudEmpty: true,
  });

  useEffect(() => {
    if (!isOpen || generationInFlightRef.current) return;
    if (inputSourceSeededRef.current) return;
    if (sourceImage) {
      setInputSourceImage(sourceImage);
      inputSourceSeededRef.current = true;
    }
  }, [isOpen, sourceImage, isProcessing]);

  useEffect(() => {
    if (isOpen && generationInFlightRef.current) {
      setIsProcessing(true);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      if (!generationInFlightRef.current) {
        setResultUrl(null);
        setSliderPosition(50);
        setProgressDesc('');
        setProgressPercent(0);
        setZoomLevel(1);
        setPanX(0);
        setPanY(0);
        setSelectedHistoryId(null);
        setCompareOriginalImage(null);
        if (!generationInFlightRef.current) {
          setInputSourceImage(null);
          inputSourceSeededRef.current = false;
        }
      }
    }
  }, [isOpen]);

  const handleGenerate = async () => {
    if (generationInFlightRef.current) return;
    const activeImage = inputSourceImage || sourceImage;
    if (!activeImage) {
      showToast(t('No image available', '没有可用的图片'), 'warning');
      return;
    }
    if (!isSupportedUploadImageDataUrl(activeImage)) {
      showToast(getUnsupportedUploadImageFormatMessage(language === 'en' ? 'en' : 'zh'), 'warning');
      return;
    }

    generationInFlightRef.current = true;
    setIsProcessing(true);
    try {
      const deductResult = await deduct('physical_reconstruct', t('Physical Reconstruction', '物理重构'));
      if (!deductResult?.success) {
        return;
      }

      setResultUrl(null);
      setProgressPercent(12);
      setProgressDesc(t('Submitting physical reconstruction...', '正在提交物理重构任务...'));
      try {
        const dimensions = await getImageDimensions(activeImage);
        const clientItemId =
          isAuthenticated && userEmail?.trim()
            ? createRunningHubClientItemId('physical_reconstruct', userEmail, true)
            : '';
        const jobMeta =
          clientItemId ?
            {
              clientItemId,
              historyPayloadDraft: {
                id: clientItemId,
                originalImage: activeImage,
                timestamp: Date.now(),
              },
            }
          : undefined;
        const resp = await api.submitPhysicalReconstructRunningHub(
          activeImage,
          dimensions,
          userEmail?.trim() || undefined,
          jobMeta,
        );
        const urls: string[] = Array.isArray((resp as any)?.urls) ? (resp as any).urls : [];
        if (urls.length < 4) throw new Error(t('Not enough images returned', '返回图片数量不足（期望4张）'));
        const historyArchived = !!(resp as any)?.historyArchived;
        const baseTs = Date.now();
        let persistedOriginal = activeImage;
        let skipCloudSyncIds: string[] | undefined;
        if (isInlineDataImageUrl(activeImage) && isAuthenticated && clientItemId) {
          const firstHistoryItem = {
            id: `${clientItemId}-0`,
            originalImage: activeImage,
            upscaledUrl: urls[0] || '',
            modeLabel: historyLabelByIndex(0),
            timestamp: baseTs,
          };
          const stampedFirst =
            stampRunningHubHistoryOwner(firstHistoryItem, userEmail) ?? firstHistoryItem;
          persistedOriginal = await persistPhysicalOriginalImageUrl(activeImage, true, clientItemId, {
            historyArchived,
            firstHistoryItem: historyArchived ? undefined : stampedFirst,
            userEmailForLog: userEmail,
            currentUserEmail: userEmail,
          });
          if (!historyArchived && !isInlineDataImageUrl(persistedOriginal)) {
            skipCloudSyncIds = [`${clientItemId}-0`];
          }
        }
        appendPhysicalHistory(persistedOriginal, urls.slice(0, 4), {
          skipCloudSync: historyArchived,
          clientItemId: clientItemId || undefined,
          skipCloudSyncIds: historyArchived ? undefined : skipCloudSyncIds,
        });
        if (historyArchived) {
          await refreshPhysicalHistoryFromCloudOnce();
        }
        const diag = (resp as { historyArchiveDiagnostics?: { expected?: number; archived_count?: number; missing_labels?: string[] } })
          ?.historyArchiveDiagnostics;
        if (diag && Array.isArray(diag.missing_labels) && diag.missing_labels.length > 0) {
          console.warn('[physical-reconstruct] history archive partial', diag);
        }
        setSelectedHistoryId(null);
        setResultUrl(urls[0]);
        setCompareOriginalImage(null);
        setProgressPercent(100);
        setProgressDesc(t('Generation completed', '生成完成'));
        showToast(t('Generation completed', '生成完成'), 'success');
      } catch (error: any) {
        setProgressPercent(0);
        setProgressDesc(error?.message || t('Generation failed', '生成失败'));
        showToast(error?.message || t('Generation failed', '生成失败'), 'error');
      }
    } finally {
      generationInFlightRef.current = false;
      setIsProcessing(false);
    }
  };

  const handleSelectHistoryItem = (item: (typeof history)[0]) => {
    const orig = resolveUsableHistoryOriginalImage(item);
    setSelectedHistoryId(item.id);
    setResultUrl(item.upscaledUrl);
    setInputSourceImage(orig);
    setCompareOriginalImage(orig);
    setSliderPosition(50);
    setZoomLevel(1);
    setPanX(0);
    setPanY(0);
  };

  useRunningHubPanelRecovery({
    featureType: 'physical_reconstruct',
    recoveryRequest,
    mapPayloadToItem: (p) => {
      const id = String(
        p.id ||
          createScopedRunningHubClientItemId('physical_reconstruct', userEmail, !!isAuthenticated),
      );
      if (isAggregatePhysicalReconstructHistoryId(id)) return null;
      const fromUpscaled =
        typeof p.upscaledUrl === 'string' ? p.upscaledUrl.trim() : '';
      if (!fromUpscaled) return null;
      let modeLabel = normalizePhysicalReconstructModeLabel(
        typeof p.modeLabel === 'string' ? p.modeLabel : undefined,
      );
      if (!modeLabel) {
        const suffix = id.match(/-([0-3])$/);
        if (suffix) modeLabel = normalizePhysicalReconstructModeLabel(historyLabelByIndex(Number(suffix[1])));
      }
      if (!modeLabel) return null;
      return {
        id,
        originalImage: String(p.originalImage || ''),
        upscaledUrl: fromUpscaled,
        modeLabel,
        timestamp: Number(p.timestamp || Date.now()),
        thumbUrl:
          typeof p.thumbUrl === 'string' && isRunningHubListThumbUrl(p.thumbUrl) ? p.thumbUrl : undefined,
      };
    },
    mergeHistory: (item) => {
      setHistory((prev) => {
        const cleaned = cleanExpired(prev);
        const updated = applyPhysicalHistoryDisplayFilter(
          [item, ...cleaned.filter((x) => x.id !== item.id)].slice(0, RUNNINGHUB_LOCAL_HISTORY_MAX_ITEMS),
        );
        writeRunningHubHistoryListToLocalStorage(
          'physical_reconstruct',
          userEmail,
          updated as unknown as Record<string, unknown>[],
        );
        return updated;
      });
    },
    applyItem: handleSelectHistoryItem,
  });

  const handleDownloadHistoryItem = async (e: React.MouseEvent, item: (typeof history)[0]) => {
    e.stopPropagation();
    try {
      showToast(t('Adding watermark...', '正在添加水印...'), 'info');
      const wm = await addWatermarkToImage(item.upscaledUrl);
      const link = document.createElement('a');
      link.href = wm;
      link.download = generatedImageDownloadFilename(item.timestamp);
      link.click();
      showToast(t('Download started', '下载已开始'), 'success');
    } catch {
      showToast(
        t(
          'Download failed. Sign in and retry if the image is from cloud storage.',
          '下载失败，请登录账号后重试（云端图片需后端拉取才能加水印）。',
        ),
        'error',
      );
    }
  };

  const handleDeleteHistoryItem = (e: React.MouseEvent, itemId: string) => {
    e.stopPropagation();
    if (!confirm(t('Delete this history entry?', '确定要删除这条历史记录吗？'))) return;
    void deleteRunningHubHistoryFromCloud('physical_reconstruct', !!isAuthenticated, itemId);
    setHistory((prev) => {
      const updated = applyPhysicalHistoryDisplayFilter(prev.filter((x) => x.id !== itemId));
      writeRunningHubHistoryListToLocalStorage(
        'physical_reconstruct',
        userEmail,
        updated as unknown as Record<string, unknown>[],
      );
      return updated;
    });
    setSelectedHistoryId((cur) => {
      if (cur !== itemId) return cur;
      setResultUrl(null);
      setCompareOriginalImage(null);
      setInputSourceImage(inputSourceSeededRef.current ? sourceImage : null);
      return null;
    });
  };

  // 滚轮缩放
  useEffect(() => {
    if (!resultUrl || !imageContainerRef.current) return;
    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      const newZoom = Math.max(0.5, Math.min(5, zoomLevel + delta));
      setZoomLevel(newZoom);
      if (newZoom <= 1) {
        setPanX(0);
        setPanY(0);
      }
    };
    const container = imageContainerRef.current;
    container.addEventListener('wheel', handleWheel, { passive: false });
    return () => container.removeEventListener('wheel', handleWheel as any);
  }, [resultUrl, zoomLevel, panX, panY]);

  useEffect(() => {
    if (!resultUrl || !imageContainerRef.current) return;
    return attachComparePanelPinchZoom(imageContainerRef.current, {
      getZoom: () => zoomLevelPinchRef.current,
      setZoom: (z) => {
        zoomLevelPinchRef.current = z;
        setZoomLevel(z);
        if (z <= 1) {
          setPanX(0);
          setPanY(0);
        }
      },
      shouldIgnoreTouchTarget: (t) =>
        !!(t && typeof (t as HTMLElement).closest === 'function' && (t as HTMLElement).closest('.slider-handle')),
    });
  }, [resultUrl]);

  // 拖拽平移（与文本重构一致）
  useEffect(() => {
    if (!resultUrl || !imageContainerRef.current) return;
    const container = imageContainerRef.current;

    const onDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (target?.closest('.cursor-ew-resize') || target?.closest('.slider-handle')) return;
      if (zoomLevel <= 1) return;
      e.preventDefault();
      setIsPanning(true);
      setPanStart({ x: e.clientX - panX, y: e.clientY - panY });
    };
    const onMove = (e: MouseEvent) => {
      if (!isPanning) return;
      setPanX(e.clientX - panStart.x);
      setPanY(e.clientY - panStart.y);
    };
    const onUp = () => setIsPanning(false);
    container.addEventListener('mousedown', onDown);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      container.removeEventListener('mousedown', onDown);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [resultUrl, zoomLevel, isPanning, panX, panY, panStart]);

  const handleDownload = async () => {
    if (!resultUrl) return;
    try {
      const wm = await addWatermarkToImage(resultUrl);
      const link = document.createElement('a');
      link.href = wm;
      link.download = generatedImageDownloadFilename(Date.now());
      link.click();
    } catch {
      showToast(
        t(
          'Download failed. Sign in and retry if the image is from cloud storage.',
          '下载失败，请登录账号后重试（云端图片需后端拉取才能加水印）。',
        ),
        'error',
      );
    }
  };

  if (!isOpen) return null;

  const panelBg = isDarkMode ? 'bg-black' : 'bg-white';
  const textPrimary = isDarkMode ? 'text-white' : 'text-zinc-900';
  const textFaint = isDarkMode ? 'text-white/50' : 'text-zinc-500';
  const cardBg = isDarkMode ? 'border-white/10 bg-white/5' : 'border-zinc-200 bg-zinc-50';

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[2400] flex"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div className="absolute inset-0 bg-black/80" onClick={onClose} />
          <motion.div
            className={`relative w-full h-full ${panelBg}`}
            initial={{ x: 40, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: 40, opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            {/* 顶栏 */}
            <div className="absolute left-0 top-0 right-0 h-16 flex items-center justify-between px-6 border-b border-white/10">
              <div className="flex items-center gap-3">
                <h2 className={`text-[18px] font-black tracking-tight ${textPrimary}`}>
                  {t('PHYSICAL RECONSTRUCTION', '物理重构')}
                </h2>
                <div className="w-2 h-2 rounded-full" style={{ background: runningHubLinked ? '#22c55e' : '#6b7280' }} />
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleDownload}
                  disabled={!resultUrl || controlsDisabled}
                  className="px-4 py-2 bg-[#D7CDA1] text-[#101820] font-bold text-xs tracking-wider uppercase hover:bg-[#D7CDA1] disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  style={{ borderRadius: '5px' }}
                >
                  {t('Download', '下载')}
                </button>
                <button
                  onClick={onClose}
                  className={`p-2 transition-all ${
                    isDarkMode ? 'bg-white/10 hover:bg-white/20 text-white' : 'bg-zinc-200 hover:bg-zinc-300 text-zinc-900'
                  }`}
                  style={{ borderRadius: '5px' }}
                  aria-label={t('Close', '关闭')}
                >
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* 左侧控制面板 */}
            <div
              ref={containerRef}
              className="absolute left-0 top-16 bottom-0 w-80 border-r border-white/10 px-6 py-6 overflow-y-auto"
            >
              <div className="mb-6">
                <h3 className={`text-[10px] font-semibold uppercase tracking-wider mb-3 ${textFaint}`}>
                  {t('INPUT SOURCE', '输入源')}
                </h3>
                <div
                  className={`aspect-[3/4] overflow-hidden border ${cardBg} relative cursor-pointer select-none`}
                  style={{ borderRadius: '10px' }}
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    if (controlsDisabled) return;
                    fileInputRef.current?.click();
                  }}
                  onKeyDown={(e) => {
                    if (controlsDisabled) return;
                    if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click();
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onDrop={(e) => {
                    if (controlsDisabled) return;
                    e.preventDefault();
                    e.stopPropagation();
                    const f = e.dataTransfer?.files?.[0];
                    if (f) void replaceCurrentImageFromFile(f);
                  }}
                >
                  {historyOriginalMissing ? (
                    <motion.div className="w-full h-full flex items-center justify-center text-center px-4 text-orange-400 text-xs">
                      {t('Original image for this history entry is unavailable', '该历史原图不可用')}
                    </motion.div>
                  ) : inputSourceDisplay ? (
                    <img
                      src={inputSourceDisplay}
                      alt="Source"
                      className="w-full h-full object-contain"
                      draggable={false}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-white/40 text-xs">
                      {t('Click or drop an image', '点击或拖入图片')}
                    </div>
                  )}
                  <div className="absolute bottom-2 right-2 text-[10px] text-white/60 bg-black/40 px-2 py-1"
                       style={{ borderRadius: '6px' }}>
                    {t('Click / Drop', '点击/拖入')}
                  </div>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/webp,.jpg,.jpeg,.png,.webp"
                  className="hidden"
                  onChange={onPickFile}
                />
              </div>

              {/* 视觉保持 4 个按钮不变；交互合并为 1 次点击生成 4 张 */}
              <button
                type="button"
                onClick={handleGenerate}
                disabled={controlsDisabled || !sourceImage}
                className="w-full text-left space-y-3 disabled:opacity-50 disabled:cursor-not-allowed border border-[#D7CDA1] p-3"
                style={{ borderRadius: '12px' }}
              >
                <div
                  className="w-full py-5 bg-[#D7CDA1]/20 text-[#D7CDA1] font-bold text-sm tracking-wider uppercase transition-all"
                  style={{ borderRadius: '9px' }}
                >
                  <div className="text-center">{modeLabelByMode.dusk}</div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div
                    className="w-full py-3 font-semibold text-xs tracking-wider transition-all bg-[#D7CDA1]/20 text-[#D7CDA1]"
                    style={{ borderRadius: '9px' }}
                  >
                    <div className="text-center">{modeLabelByMode.day}</div>
                  </div>
                  <div
                    className="w-full py-3 font-semibold text-xs tracking-wider transition-all bg-[#D7CDA1]/20 text-[#D7CDA1]"
                    style={{ borderRadius: '9px' }}
                  >
                    <div className="text-center">{modeLabelByMode.night}</div>
                  </div>
                </div>

                <div
                  className="w-full py-5 bg-[#D7CDA1]/20 text-[#D7CDA1] font-bold text-sm tracking-wider uppercase transition-all"
                  style={{ borderRadius: '9px' }}
                >
                  <div className="text-center">{modeLabelByMode.cloudy}</div>
                </div>
              </button>

              {/* 说明文字 */}
              <div className={`text-[10px] leading-relaxed mt-4 font-normal ${textFaint}`}>
                <p>{t('Note: Generation may take several minutes. Please wait patiently.', '注意：生成可能需要几分钟时间，请耐心等待。')}</p>
                <p className="mt-2 text-[#D7CDA1]">
                  {t(
                    'AI generation may take multiple tries due to the algorithm. If you are not satisfied, click the button to generate again.',
                    'AI生成因算法原因，并非一次达到满意效果，如不满意，点击按钮继续生成。',
                  )}
                </p>
                <p className={`mt-2 text-[10px] font-normal ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`}>
                  {t(
                    'If the preview loads incorrectly, try switching to another image, then return here to check.',
                    '图片加载异常时，请尝试切换其他图片后再返回查看。',
                  )}
                </p>
                <div
                  className={`mt-2 text-[9px] font-normal leading-relaxed ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}
                >
                  {language === 'en' ? (
                    <>
                      <p>Physical photo / dusk: 4 images per generate click</p>
                      <p className="mt-0.5">Including:</p>
                      <p>Physical photo / day</p>
                      <p>Physical photo / night</p>
                      <p>Physical photo / cloudy</p>
                      <p>Physical photo / dusk</p>
                    </>
                  ) : (
                    <>
                      <p>物理照片/黄昏：点击一次生成 4 张图片</p>
                      <p className="mt-0.5">其中包含：</p>
                      <p>物理照片/白天</p>
                      <p>物理照片/夜晚</p>
                      <p>物理照片/阴天</p>
                      <p>物理照片/黄昏</p>
                    </>
                  )}
                </div>
              </div>

              {/* 进度条显示 */}
              {isProcessing && (
                <div className="mt-6">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-[9px] text-white/60 font-semibold">{t('Progress', '进度')}</span>
                    <span className="text-[9px] text-[#D7CDA1] font-semibold">{Math.round(progressPercent)}%</span>
                  </div>
                  <div className="w-full h-2 bg-black/40 rounded-full overflow-hidden">
                    <div className="h-full bg-[#D7CDA1] transition-all duration-300 ease-out" style={{ width: `${progressPercent}%` }} />
                  </div>
                  <p className="text-[8px] text-white/50 mt-1 font-normal">{progressDesc}</p>
                </div>
              )}

              {/* 历史记录区域 */}
              <div className="mt-6 pt-6 border-t border-white/10">
                <h3 className="text-[10px] font-semibold uppercase tracking-wider text-white/50 mb-3">
                  {t('Physical Reconstruction History', '物理重构历史')}
                </h3>
                {history.length === 0 ? (
                  <div className="text-[10px] text-white/50 text-center py-4 font-normal">
                    {t('No history yet', '暂无物理重构历史')}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {history.map((item) => (
                      <div
                        key={item.id}
                        role="button"
                        tabIndex={0}
                        onClick={() => {
                          if (!controlsDisabled) handleSelectHistoryItem(item);
                        }}
                        onKeyDown={(e) => {
                          if (controlsDisabled) return;
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleSelectHistoryItem(item);
                          }
                        }}
                        className={`w-full flex items-center gap-3 p-2 border transition outline-none ${
                          selectedHistoryId === item.id ? 'border-[#D7CDA1] bg-[#D7CDA1]/10' : 'border-white/10 hover:border-white/20'
                        } ${controlsDisabled ? 'opacity-80' : 'cursor-pointer'}`}
                        style={{ borderRadius: '10px' }}
                      >
                        <div className="w-12 h-12 rounded-md shrink-0 overflow-hidden bg-white/10 relative">
                          <RunningHubHistoryListThumb item={item} imgClassName="w-full h-full object-cover" />
                        </div>
                        <div className="flex-1 text-left min-w-0">
                          <div className="text-[11px] text-white/80 font-semibold">{item.modeLabel}</div>
                          <div className="text-[9px] text-white/40">{new Date(item.timestamp).toLocaleString()}</div>
                          <div className="mt-1">
                            <RunningHubCosExpiryBadge timestamp={item.timestamp} lang={language === 'en' ? 'en' : 'zh'} />
                          </div>
                        </div>
                        <div className="flex flex-col gap-1.5 shrink-0" onClick={(e) => e.stopPropagation()}>
                          <button
                            type="button"
                            onClick={(e) => void handleDownloadHistoryItem(e, item)}
                            className="px-3 py-1.5 bg-[#D7CDA1] text-[#101820] text-[8px] font-semibold uppercase hover:bg-[#D7CDA1] transition-all"
                            style={{ borderRadius: '4px' }}
                          >
                            {t('Download', '下载')}
                          </button>
                          <button
                            type="button"
                            onClick={(e) => handleDeleteHistoryItem(e, item.id)}
                            className="px-3 py-1.5 bg-red-600 text-white text-[8px] font-semibold uppercase hover:brightness-110 transition-all"
                            style={{ borderRadius: '4px' }}
                          >
                            {t('Delete', '删除')}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* 右侧对比区（滑杆逻辑与文本重构一致） */}
            <div className="absolute left-80 top-16 right-0 bottom-0">
              <div className="w-full h-full flex items-center justify-center">
                {!sourceImage ? (
                  <div className="text-white/40 text-sm">{t('Please load an image first.', '请先加载图片。')}</div>
                ) : (
                  <div className="relative w-full h-full">
                    <div ref={imageContainerRef} className="absolute inset-0 overflow-hidden touch-none">
                      <div ref={imageWrapperRef} className="absolute inset-0 flex items-center justify-center touch-none">
                        {historyOriginalMissing ? (
                          <motion.div className="absolute inset-0 flex items-center justify-center text-center px-6 text-orange-400 text-sm z-0">
                            {t('Original image for this history entry is unavailable', '该历史原图不可用')}
                          </motion.div>
                        ) : (
                          <img
                            ref={originalImageRef}
                            src={compareOriginalDisplay || ''}
                            alt="original"
                            className="max-w-full max-h-full object-contain select-none pointer-events-none"
                            draggable={false}
                          />
                        )}
                        {/* 结果图（仅 UI：当前为空时隐藏） */}
                        {resultUrl && (
                          <div
                            className="absolute inset-0 overflow-hidden"
                            style={{ clipPath: `inset(0 ${100 - sliderPosition}% 0 0)` }}
                          >
                            <img
                              ref={upscaledImageRef}
                              src={resultUrl}
                              alt="result"
                              className="w-full h-full object-contain select-none pointer-events-none"
                              draggable={false}
                            />
                          </div>
                        )}
                      </div>
                    </div>

                    {/* 滑杆：Pointer Events + 加宽命中区 */}
                    {resultUrl && (
                      <div className="absolute inset-0 pointer-events-none">
                        <div
                          className="absolute top-0 bottom-0 z-10 flex justify-center cursor-ew-resize touch-none slider-handle pointer-events-auto"
                          style={{
                            left: `${sliderPosition}%`,
                            width: 44,
                            marginLeft: -22,
                          }}
                          onPointerDown={(e) =>
                            startCompareSliderPointerDragFromWrapper(e, {
                              wrapperRef: imageWrapperRef,
                              setSliderPercent: setSliderPosition,
                            })
                          }
                          onTouchStart={(e) =>
                            startCompareSliderTouchDragFromWrapper(e, {
                              wrapperRef: imageWrapperRef,
                              setSliderPercent: setSliderPosition,
                            })
                          }
                        >
                          <div className="w-0.5 h-full shrink-0 bg-[#D7CDA1] self-center pointer-events-none" />
                          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-[#D7CDA1] text-[#101820] flex items-center justify-center pointer-events-none text-sm font-semibold">
                            ⇆
                          </div>
                        </div>
                      </div>
                    )}

                    {/* 下载：已移至顶栏（与高清放大一致） */}
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PhysicalReconstructPanel;

